/**
 * Indirection so the API client can read the access token without importing the
 * auth store (which imports the client) — keeps the module graph acyclic.
 * `registerAuthTokenProvider` is called once at boot in `main.tsx`.
 */

import { ApiError } from './errors'

type TokenProvider = () => string | null
type UnauthorizedHandler = () => void

let getToken: TokenProvider = () => null
let onUnauthorized: UnauthorizedHandler = () => {}

export function registerAuthTokenProvider(
  provider: TokenProvider,
  unauthorizedHandler: UnauthorizedHandler,
) {
  getToken = provider
  onUnauthorized = unauthorizedHandler
}

export function readAccessToken() {
  return getToken()
}

export function notifyUnauthorized() {
  onUnauthorized()
}

/** Marks a request rejected locally because the session token was unavailable. */
export const MISSING_AUTH_TOKEN_CODE = 'MISSING_AUTH_TOKEN'
export const STALE_AUTH_REQUEST_CODE = 'STALE_AUTH_REQUEST'

function findAuthorization(headers: Record<string, string> | undefined) {
  for (const [key, value] of Object.entries(headers ?? {})) {
    if (key.toLowerCase() === 'authorization' && value.trim().length > 0) return value
  }
  return undefined
}

/**
 * Authorization header for every protected endpoint. Callers that pass their own
 * `Authorization` (e.g. profile lookup right after login, before the session is
 * stored) keep it. Without a usable token the request is rejected here instead of
 * leaving the browser unauthenticated — the backend would answer
 * "Missing Authorization Header" and the UI would surface a confusing error.
 */
export function requireAuthHeaders(headers?: Record<string, string>): Record<string, string> {
  if (findAuthorization(headers)) return {}

  const token = readAccessToken()
  if (!token) {
    throw new ApiError(
      'Permintaan dibatalkan karena tidak ada sesi aktif.',
      401,
      MISSING_AUTH_TOKEN_CODE,
    )
  }
  return { Authorization: `Bearer ${token}` }
}

/** Only a backend 401 for the current Bearer token may end the active session. */
export function unauthorizedResponseError(headers: Record<string, string>, fallback: string) {
  const authorization = findAuthorization(headers)
  const bearer = authorization?.match(/^Bearer\s+(.+)$/i)?.[1]
  if (!bearer) return new ApiError(fallback, 401)
  if (bearer !== readAccessToken()) {
    return new ApiError('Permintaan dari sesi sebelumnya dibatalkan.', 401, STALE_AUTH_REQUEST_CODE)
  }
  notifyUnauthorized()
  return new ApiError(
    'Sesi kamu sudah berakhir. Masuk lagi untuk lanjut.',
    401,
    'SESSION_UNAUTHORIZED',
  )
}
